import React, {useState, useRef, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {useAIChatStream, type Message} from '../ai-chat/useAIChatStream';
import {EmptyState} from '../ai-chat/EmptyState';
import {ChatIntro} from '../ai-chat/ChatIntro';
import {MessageBubble} from '../ai-chat/MessageBubble';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {canOpenReportBuilder} from '../ai-report-builder/reportBuilderGate';
import {grainOf} from '../ai-report-builder/reportGrain';
import type {ReportDefinitionV1} from '../ai-report-builder/contracts/report-definition.types';
import type {ReportIntentPayload} from '../ai-report-builder/ReportBuilderContext';
import {parseReportTaxonomy, type ReportTaxonomy} from '../ai-report-builder/contracts/report-taxonomy.types';
import {D} from '../ai-chat/design-tokens';
import DrawerFooter from './DrawerFooter';
import {useAgentDrawerContext} from '../../forecast-app/shared/contexts/AgentDrawerContext';
import CreateSavedPromptMutation from '../../mutations/agent/CreateSavedPromptMutation';
import {CommitMutation} from '../../mutations/CommitMutation';
import {createToast} from '../../forecast-app/shared/components/toasts/another-toast/toaster';
import {getProjectIdFromPath} from '../../forecast-app/shared/util/UrlContextUtil';
import {useForecastFetchQuery} from '../../forecast-app/shared/hooks/useForecastFetchQuery';
import type {ChatPanelReportTaxonomyQuery$data} from './__generated__/ChatPanelReportTaxonomyQuery.graphql';
import {
	NOVA_BOOKMARK_LOCATION,
	trackNovaActionRegenerated,
	trackNovaChatBookmarked,
	trackNovaChatMessageSent,
	trackNovaChatModalOpened,
	trackNovaChatReplyRegenerated,
} from '../../tracking/amplitude/novaEvents';
import {countNovaMessage, getNovaIdentity} from '../../tracking/amplitude/novaTrackingSession';

// The enum name the backend taxonomy resolver expects (see schema.graphql
// `Viewer.reportTaxonomy(reportType:)`), and the fallback for an intent whose definition names no
// grain this builder builds.
const TASK_REPORT_TYPE = 'TASK_REPORT';

const REPORT_BUILDER_SUGGESTION_IDS = [
	'nova.report_builder_intro.suggestion.report_types',
	'nova.report_builder_intro.suggestion.tasks_by_assignee',
	'nova.report_builder_intro.suggestion.blocked_overdue_tasks',
	'nova.report_builder_intro.suggestion.tasks_for_client',
	'nova.report_builder_intro.suggestion.active_projects_by_client',
] as const;

const chatPanelReportTaxonomyQuery = graphql`
	query ChatPanelReportTaxonomyQuery($reportType: String) {
		viewer {
			reportTaxonomy(reportType: $reportType)
		}
	}
`;

const MessagesArea = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 20px;
	background: ${D.bg};
	position: relative;
`;

const ScrollToBottomBtn = styled.button`
	position: absolute;
	bottom: 12px;
	left: 50%;
	transform: translateX(-50%);
	background: ${D.bg};
	border: 1px solid ${D.border};
	border-radius: 9999px;
	padding: 4px 12px;
	font-size: 11px;
	color: ${D.textMuted};
	cursor: pointer;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
	display: flex;
	align-items: center;
	gap: 4px;

	&:hover {
		background: ${D.surface};
	}
`;

const PanelWrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
`;

// The create-project patch is untyped JSON from the agent, so every field can arrive as something
// other than a string. Forward only strings: the modal parses these (lower-casing the budget type,
// handing the dates to moment) and a stray number there throws while the modal is mounting.
const asString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

// A plain non-negative decimal, as the agent writes a price ("50000", "1234.5"). Anchored so that
// '', 'NaN', 'Infinity', '-5' and '1e5' are refused rather than coerced by Number().
const PLAIN_AMOUNT = /^\d+(\.\d+)?$/;

// The fixed price amount for the modal's price field. Accepts the string the agent sends, or a
// number, and drops anything that is not a usable price so the modal opens with an empty field.
const asAmount = (value: unknown): number | undefined => {
	if (typeof value === 'number') {
		return Number.isFinite(value) && value >= 0 ? value : undefined;
	}
	return typeof value === 'string' && PLAIN_AMOUNT.test(value.trim()) ? Number(value.trim()) : undefined;
};

// Card outcomes after which the write may have reached the server.
const APPLIED_STATUSES: ReadonlySet<string> = new Set(['done', 'partial', 'unresolved']);

function appliedAWrite(m: Message): boolean {
	return (
		!!m.actionPlan &&
		m.actionResolved === true &&
		!m.actionDismissed &&
		!!m.actionResult &&
		APPLIED_STATUSES.has(m.actionResult.status)
	);
}

/** Whether the turn regenerate would re-run already applied a write. Regenerate re-sends the last
 * typed message on a fresh thread, which knows nothing of that outcome, so it stages the same write
 * again: a duplicate the user could confirm. A cancelled, failed or expired card applied nothing. */
function lastTypedTurnAppliedAWrite(msgs: Message[]): boolean {
	const lastUserRev = [...msgs].reverse().findIndex(m => m.role === 'user' && !m.hidden);
	if (lastUserRev === -1) return false;
	return msgs.slice(msgs.length - lastUserRev).some(appliedAWrite);
}

/** The same check for a card's own Regenerate Reply: it re-sends the typed message the card answers,
 * and a multi-step request may have applied an earlier card in between. */
function cardRegenerateRepeatsAWrite(msgs: Message[], actionId: string): boolean {
	const cardIdx = msgs.findIndex(m => m.actionPlan?.action_id === actionId);
	if (cardIdx === -1) return false;
	let userIdx = cardIdx;
	while (userIdx >= 0 && !(msgs[userIdx].role === 'user' && !msgs[userIdx].hidden)) userIdx--;
	return msgs.slice(userIdx + 1, cardIdx).some(appliedAWrite);
}

type ChatPanelProps = {
	viewerPersonId?: number;
	companyId?: string;
	companyName?: string;
	history: {push: (path: string, state?: unknown) => void} | null | undefined;
	onRegisterClear?: (clearFn: () => void) => void;
	onRegisterRegenerate?: (regenerateFn: () => void) => void;
	/** Whether the kebab may offer Regenerate Last Reply: there is a reply, and re-running it would not repeat an applied write. */
	onCanRegenerateChange?: (canRegenerate: boolean) => void;
};

const ChatPanel = ({
	viewerPersonId,
	companyId,
	companyName,
	history,
	onRegisterClear,
	onRegisterRegenerate,
	onCanRegenerateChange,
}: ChatPanelProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {inputValue, setInputValue, savedPromptTexts, setSavedPromptTexts, registerReportIntentSavedRecorder, introVariant} =
		useAgentDrawerContext();
	const [input, setInput] = useState('');
	const [showScrollBtn, setShowScrollBtn] = useState(false);
	// Once a message is sent while the report-builder intro is showing — a suggestion click or a
	// normal typed send — the intro is consumed and must not keep appearing below whatever gets
	// sent afterward. Without this, `messages.map()` always renders before the intro (see the
	// render below), so anything sent through it would otherwise appear ABOVE the intro rather than
	// replacing it. Reset to false each time the CTA opens the drawer with this variant, so a later
	// CTA click starts fresh regardless of whether an earlier one was already dismissed.
	const [reportBuilderIntroDismissed, setReportBuilderIntroDismissed] = useState(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (inputValue) {
			setInput(inputValue);
			setInputValue('');
			inputRef.current?.focus();
		}
	}, [inputValue, setInputValue]);

	const handleProjectIntent = useCallback((data: Record<string, unknown>) => {
		const patch = (data?.patch ?? {}) as Record<string, unknown>;
		const noRateCard = patch.noRateCard;
		showModal({
			type: MODAL_TYPE.NEW_PROJECT_V2,
			initialName: asString(patch.name),
			initialStartDate: asString(patch.startDate),
			initialEndDate: asString(patch.endDate),
			initialBudgetType: asString(patch.budgetType),
			// Shown only when the budget type resolves to fixed price - the modal has no price field
			// under any other type.
			initialFixedPriceBudget: asAmount(patch.budget),
			// The rate card arrives as the name the user said, not an id - the modal matches it
			// against the company's cards. Without this the modal always opens on the company
			// default, so a named card silently turned into the wrong one. The "no rate card"
			// flag is accepted as either a boolean or its string form.
			initialRateCardName: asString(patch.rateCard),
			initialNoRateCard: noRateCard === true || noRateCard === 'true',
		});
		trackNovaChatModalOpened();
	}, []);

	// `setMessages` only exists once the hook below runs; hold it in a ref (populated by an effect
	// after the hook) so this callback — which is passed INTO the hook — can push a card.
	const pushIntentMessageRef = useRef<((updater: (prev: Message[]) => Message[]) => void) | null>(null);

	// Taxonomy for the current report intent — fetched lazily (not blocking the card render) so the
	// condition summary can upgrade from the agent's own `summaries[].text` to a taxonomy-driven
	// render once the fetch resolves. A single value (not per-message) is sufficient: only one
	// report intent is ever 'pending' at a time (see the supersede filter below), and confirmed/
	// cancelled cards are historical records that don't need a fresher taxonomy.
	const [reportTaxonomy, setReportTaxonomy] = useState<ReportTaxonomy | undefined>(undefined);
	const {data: reportTaxonomyQueryData, fetch: fetchReportTaxonomy} = useForecastFetchQuery<
		ChatPanelReportTaxonomyQuery$data,
		{reportType: string}
	>(chatPanelReportTaxonomyQuery);

	// The hook's `fetch` takes an onSuccess callback but does not hand it the resolved data (it only
	// signals completion) — react to the returned `data` instead once Relay resolves it. Parse
	// defensively: a malformed/empty JSON string must not crash the chat surface, it should just
	// leave the card on its non-taxonomy fallback (summaries/prompt).
	useEffect(() => {
		const raw = reportTaxonomyQueryData?.viewer?.reportTaxonomy;
		if (!raw) return;
		try {
			const parsed = parseReportTaxonomy(raw);
			if (parsed.fields && parsed.fields.length > 0) {
				setReportTaxonomy(parsed);
			}
		} catch {
			// leave reportTaxonomy as-is — card falls back to summaries/prompt
		}
	}, [reportTaxonomyQueryData]);

	const handleReportIntent = useCallback(
		(data: Record<string, unknown>) => {
			if (!canOpenReportBuilder()) return;
			// A create_report_intent tool result whose content is the JSON literal `null` (or any
			// non-object) parses without throwing; guard so no malformed card is surfaced.
			if (!data || typeof data !== 'object') return;
			// Do NOT open the builder directly. Surface a HITL confirm card in the chat (mirroring
			// write-action confirmations); the builder opens only once the user confirms and clicks View
			// — see MessageBubble → ReportIntentCard and the onViewReportIntent handler below.
			const id = `report-intent-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
			pushIntentMessageRef.current?.(prev => [
				// Supersede any still-pending report proposal: a refined intent replaces the prior
				// unactioned card instead of stacking a second one. Confirmed/cancelled cards are
				// terminal records of a decision the user already made, so they are kept as history.
				...prev.filter(m => !(m.reportIntent && m.reportIntent.status === 'pending')),
				{id, role: 'assistant', content: '', reportIntent: {payload: data, status: 'pending'}},
			]);
			// Fetch the taxonomy alongside the card, not before it — the card renders immediately from
			// `summaries`/`prompt` (see ReportIntentCard) and upgrades in place once (if) the fetch
			// above resolves and the effect parses it.
			//
			// FOR THE INTENT'S OWN GRAIN, not a hardcoded TASK_REPORT: the builder has built
			// project-status reports for a while now, and handing the card the task catalog names its
			// condition fields and grouping tokens from the wrong grain's vocabulary — every lookup
			// misses, so the card falls back to raw enum values and raw tokens.
			//
			// The definition is agent-authored JSON, so `fields` is only trusted when it is actually an
			// array: `grainOf` iterates it, and a non-iterable value there (an object, a number) would
			// throw. The throw is swallowed by the tool-result handler that calls this, which would drop
			// the taxonomy fetch entirely and leave the card on its raw fallback for good.
			const intentDefinition = (data as {definition?: unknown}).definition;
			const definitionFields = (intentDefinition as {fields?: unknown} | null | undefined)?.fields;
			const grainIsReadable =
				!!intentDefinition &&
				typeof intentDefinition === 'object' &&
				(definitionFields === null || definitionFields === undefined || Array.isArray(definitionFields));
			const grain = grainIsReadable
				? grainOf(intentDefinition as Pick<ReportDefinitionV1, 'reportType' | 'fields'>)
				: null;
			fetchReportTaxonomy({reportType: grain ?? TASK_REPORT_TYPE});
		},
		[fetchReportTaxonomy]
	);

	const handleProjectDeleted = useCallback(
		(_relayNodeId: string, customProjectId?: string) => {
			if (!history) return;
			if (!window.location.pathname.startsWith('/project/')) return;
			// Only navigate away if we're viewing the specific project that was deleted.
			// getProjectIdFromPath extracts the customProjectId slug from the URL (e.g. "/project/P-42/..." → "P-42").
			// If the slug matches the deleted project's customProjectId, navigate to the list.
			// If slug extraction fails (URL without sub-path) or customProjectId is unknown, navigate anyway
			// — the deleted project is gone and any /project/* page would be broken.
			if (customProjectId) {
				const currentSlug = getProjectIdFromPath(window.location.pathname);
				if (currentSlug !== null && currentSlug !== customProjectId) return;
			}
			history.push('/projects');
		},
		[history]
	);

	const {
		messages,
		setMessages,
		isStreaming,
		isHitlPending,
		sendMessage,
		respondToAction,
		applyReportPick,
		abort,
		clearMessages,
		resetThread,
	} = useAIChatStream({
		viewerPersonId,
		companyId,
		companyName,
		onProjectIntent: handleProjectIntent,
		onReportIntent: handleReportIntent,
		onProjectDeleted: handleProjectDeleted,
	});

	const hasLlmResponse = messages.some(m => m.role === 'assistant');
	const canRegenerate = hasLlmResponse && !lastTypedTurnAppliedAWrite(messages);

	// Abort any in-flight agent run when the panel unmounts (e.g. after navigation triggered by handleProjectDeleted),
	// preventing state updates on an unmounted component from the still-running SSE stream.
	const abortRef = useRef(abort);
	useEffect(() => {
		abortRef.current = abort;
	}, [abort]);
	useEffect(() => () => abortRef.current(), []);

	useEffect(() => {
		onCanRegenerateChange?.(canRegenerate);
	}, [canRegenerate, onCanRegenerateChange]);

	// Bumped whenever the conversation is replaced (Clear, Regenerate), so an awaited candidate pick
	// can tell that the chat it was made in is gone and drop its result rather than act on the new one.
	const threadGenerationRef = useRef(0);

	useEffect(() => {
		onRegisterClear?.(() => {
			threadGenerationRef.current += 1;
			clearMessages();
			setInput('');
			setShowScrollBtn(false);
		});
	}, [clearMessages, onRegisterClear]);

	// Keep refs so the registered regenerate fn always sees current values without re-registering
	const messagesRef = useRef(messages);
	const isStreamingRef = useRef(isStreaming);
	const sendMessageRef = useRef(sendMessage);
	const setMessagesRef = useRef(setMessages);
	const resetThreadRef = useRef(resetThread);
	useEffect(() => {
		messagesRef.current = messages;
	}, [messages]);
	useEffect(() => {
		isStreamingRef.current = isStreaming;
	}, [isStreaming]);
	useEffect(() => {
		sendMessageRef.current = sendMessage;
	}, [sendMessage]);
	useEffect(() => {
		setMessagesRef.current = setMessages;
	}, [setMessages]);
	useEffect(() => {
		pushIntentMessageRef.current = setMessages;
	}, [setMessages]);
	useEffect(() => {
		resetThreadRef.current = resetThread;
	}, [resetThread]);

	// Resend a user query, replaying the conversation up to that point.
	// - The drawer header button calls this with no argument → resend the LAST
	//   user query.
	// - An expired action card calls this with its own actionId → resend the user
	//   query that produced THAT card, so a mid-transcript card regenerates the
	//   right prompt (not whatever the latest turn happened to be).
	// The thread is rotated first so the agent starts from a clean checkpoint —
	// otherwise a stale pending/expired action still in the graph state makes the
	// agent defer to the old card instead of producing a fresh plan.
	const regenerate = useCallback((actionId?: string) => {
		if (isStreamingRef.current) return;
		const msgs = messagesRef.current;
		let userIdx = -1;
		if (actionId) {
			// Find the card, then the nearest preceding message the user typed. A hidden follow-up
			// has no text of its own, so re-sending it on the fresh thread would ask the agent to
			// carry on with a task it no longer has any record of.
			const cardIdx = msgs.findIndex(m => m.actionPlan?.action_id === actionId);
			if (cardIdx === -1 || cardRegenerateRepeatsAWrite(msgs, actionId)) return;
			for (let i = cardIdx; i >= 0; i--) {
				if (msgs[i].role === 'user' && !msgs[i].hidden) {
					userIdx = i;
					break;
				}
			}
		} else {
			if (lastTypedTurnAppliedAWrite(msgs)) return;
			const lastUserRev = [...msgs].reverse().findIndex(m => m.role === 'user' && !m.hidden);
			if (lastUserRev !== -1) userIdx = msgs.length - 1 - lastUserRev;
		}
		if (userIdx === -1) return;
		const history = msgs.slice(0, userIdx);
		const userText = msgs[userIdx].content;
		// Captured before resetThread rotates it and emitted after, so the event carries the new
		// thread as threadId and the old one as previousThreadId.
		const previousThreadId = getNovaIdentity().threadId;
		const card = actionId ? msgs.find(m => m.actionPlan?.action_id === actionId)?.actionPlan : undefined;
		threadGenerationRef.current += 1;
		setMessagesRef.current(history);
		resetThreadRef.current();
		if (card) trackNovaActionRegenerated(card, previousThreadId);
		else trackNovaChatReplyRegenerated(previousThreadId);
		void sendMessageRef.current(userText, history);
	}, []);

	useEffect(() => {
		onRegisterRegenerate?.(regenerate);
	}, [onRegisterRegenerate, regenerate]);

	// Scroll handling
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const onScroll = () => {
			setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
		};
		el.addEventListener('scroll', onScroll);
		return () => el.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		if (!showScrollBtn) {
			messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
		}
	}, [messages, showScrollBtn]);

	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
		setShowScrollBtn(false);
	}, []);

	// The report-builder intro is appended after any existing history (see the render below), not
	// substituted for it, so opening the drawer via the CTA does not itself touch `messages` and
	// would otherwise leave the view wherever it was scrolled when the drawer was last closed —
	// potentially nowhere near the newly-appended intro. Force it into view the moment the CTA sets
	// this variant, the same way a genuinely new message would pull the view down.
	useEffect(() => {
		if (introVariant === 'reportBuilder') {
			setReportBuilderIntroDismissed(false);
			scrollToBottom();
		}
	}, [introVariant, scrollToBottom]);

	// The one card whose candidate pick is in flight, or whose last pick failed. A single slot is
	// enough: only one pick may be in flight at a time, and starting a pick clears any earlier failure.
	// A failure may carry the gateway's own sentence for it, which the card shows instead of its copy.
	const [reportPick, setReportPick] = useState<{
		messageId: string;
		phase: 'applying' | 'failed';
		failedMessage?: string;
	} | null>(null);
	// While a pick is applying nothing else may be sent: that turn would drain the hand-off before the
	// pick's row is inserted, refine the pre-pick draft, and supersede the pick.
	const pickApplying = reportPick?.phase === 'applying';

	// Shared by the footer's send button and the report-builder intro's suggestion chips: sends
	// `text` straight away rather than only populating the input (which is what the generic Nova
	// EmptyState's suggestions still do, via setInput). Also dismisses the report-builder intro —
	// sending anything while it's showing, suggestion or typed, consumes it.
	const sendText = useCallback(
		(text: string) => {
			if (isStreaming || pickApplying) return;
			setReportBuilderIntroDismissed(true);
			setInput('');
			// An empty send is stored as a hidden turn, which the analytics do not count as a message.
			if (text.trim().length > 0) {
				countNovaMessage();
				trackNovaChatMessageSent({
					messageLength: text.trim().length,
					turnNumber: messages.filter(m => m.role === 'user' && !m.hidden).length + 1,
				});
			}
			void sendMessage(text.trim(), messages); // empty string intentionally allowed — product decision
			setTimeout(() => inputRef.current?.focus(), 0);
		},
		[isStreaming, pickApplying, sendMessage, messages]
	);

	const handleSend = useCallback(() => sendText(input), [sendText, input]);

	// Chat fallback for a candidate pick the gateway cannot apply itself (see
	// handlePickReportCandidate): the pick goes out as the user's own message and the agent redrafts
	// from it. Unlike `sendText` (the footer send button and the report-builder intro's suggestion
	// chips), it must NOT clear the user's own typed draft — they may have been mid-rephrase when
	// they clicked a candidate instead. Guarded the same way DrawerFooter disables its own input:
	// while streaming a response, or while a write-action HITL approval is pending (there is no
	// stream to append the redraft to yet). Returns whether the message went out.
	const sendCandidatePick = useCallback(
		(text: string): boolean => {
			if (isStreaming || isHitlPending) return false;
			void sendMessage(text, messages);
			return true;
		},
		[isStreaming, isHitlPending, sendMessage, messages]
	);
	// The fallback runs after an awaited gateway call, so it reads the send path through a ref: the
	// closure captured at pick time holds the streaming flags and transcript from before the call.
	const sendCandidatePickRef = useRef(sendCandidatePick);
	useEffect(() => {
		sendCandidatePickRef.current = sendCandidatePick;
	}, [sendCandidatePick]);

	// Set synchronously when a pick starts, so a second click in the same tick — before the state
	// above has re-rendered — is refused too.
	const pickInFlightRef = useRef(false);
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	// A candidate pick is applied by the gateway with no model call: the card's payload goes up, the
	// patched payload comes back, and the card is updated in place — same message id, still pending.
	// The next chat turn picks the patched draft up server-side. A gateway without that endpoint, or
	// one refusing the pick for a reason that depends only on the payload (so a retry could never
	// succeed), answers `unsupported` and the pick falls back to the chat message, which the agent
	// redrafts from. Only a retryable failure leaves the card in its failed state.
	const handlePickReportCandidate = useCallback(
		async (messageId: string, term: string, candidate: string) => {
			if (isStreaming || isHitlPending || pickInFlightRef.current) return;
			const intent = messagesRef.current.find(m => m.id === messageId)?.reportIntent;
			if (!intent || intent.status !== 'pending') return;

			pickInFlightRef.current = true;
			const generation = threadGenerationRef.current;
			setReportPick({messageId, phase: 'applying'});
			const result = await applyReportPick(intent.payload, term, candidate);
			pickInFlightRef.current = false;
			if (!mountedRef.current) return;
			// The chat was cleared or regenerated while the pick was awaiting: its card is gone, and a
			// fallback message would land in the new conversation about a draft that no longer exists.
			if (threadGenerationRef.current !== generation) {
				setReportPick(null);
				return;
			}

			if (result.status === 'applied') {
				// Only a card still pending is replaced: one the user confirmed or cancelled meanwhile, or
				// that a newer draft superseded, keeps what it showed when that happened.
				setMessages(prev =>
					prev.map(m =>
						m.id === messageId && m.reportIntent?.status === 'pending'
							? {...m, reportIntent: {...m.reportIntent, payload: result.payload}}
							: m
					)
				);
				setReportPick(null);
			} else if (result.status === 'unsupported') {
				const sent = sendCandidatePickRef.current(
					formatMessage(
						{
							id: 'ai_report_builder.report_card.use_candidate',
							defaultMessage: 'Use "{candidate}" instead of "{term}".',
						},
						{candidate, term}
					)
				);
				// A reply that started, or an approval that became pending, while the pick was awaiting
				// leaves no way to send the fallback. The card shows its failure rather than the click
				// just vanishing; the candidates come back once the chat is free.
				setReportPick(sent ? null : {messageId, phase: 'failed'});
			} else {
				setReportPick({messageId, phase: 'failed', failedMessage: result.refusalMessage});
			}
		},
		[isStreaming, isHitlPending, applyReportPick, setMessages, formatMessage]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleSend();
			}
		},
		[handleSend]
	);

	const handleSavePrompt = useCallback(
		(promptText: string) => {
			const trimmed = promptText.trim();
			if (!trimmed) return;
			trackNovaChatBookmarked(NOVA_BOOKMARK_LOCATION.COMPOSER);
			const name = trimmed.length > 50 ? trimmed.substring(0, 50) : trimmed;
			CommitMutation(CreateSavedPromptMutation, {name, promptText: trimmed}, () => {
				setSavedPromptTexts(new Set([...savedPromptTexts, trimmed]));
				createToast({duration: 3000, message: formatMessage({id: 'nova.prompt.saved_toast'})});
			});
		},
		[formatMessage, savedPromptTexts, setSavedPromptTexts]
	);

	// Stamps the stored report's id onto the chat message that proposed it, so its View button stops
	// rebuilding a draft from the agent's original payload. Only the card's navigation changes — the
	// message's rendered content is a record of what the agent produced and stays as it was.
	const handleIntentSaved = useCallback(
		(messageId: string, savedReportId: string) => {
			setMessages(prev =>
				prev.map(m =>
					m.id === messageId && m.reportIntent ? {...m, reportIntent: {...m.reportIntent, savedReportId}} : m
				)
			);
		},
		[setMessages]
	);

	useEffect(
		() => registerReportIntentSavedRecorder(handleIntentSaved),
		[registerReportIntentSavedRecorder, handleIntentSaved]
	);

	return (
		<PanelWrapper>
			<MessagesArea ref={scrollRef}>
				{messages.length === 0 && introVariant === 'default' && <EmptyState onSuggestionClick={setInput} />}

				{messages.map((msg, idx) =>
					msg.hidden ? null : (
						<MessageBubble
							key={msg.id}
							message={msg}
							isLastAssistant={msg.role === 'assistant' && idx === messages.length - 1 && isStreaming}
							history={history}
							reportTaxonomy={reportTaxonomy}
							onRespondAction={respondToAction}
							onRegenerateAction={
								msg.actionPlan && cardRegenerateRepeatsAWrite(messages, msg.actionPlan.action_id)
									? undefined
									: regenerate
							}
							onResumeAction={actionId =>
								setMessages(prev =>
									prev.map(m => (m.actionPlan?.action_id === actionId ? {...m, actionPaused: false} : m))
								)
							}
							onConfirmReportIntent={messageId =>
								setMessages(prev =>
									prev.map(m =>
										m.id === messageId && m.reportIntent
											? {...m, reportIntent: {...m.reportIntent, status: 'confirmed'}}
											: m
									)
								)
							}
							onCancelReportIntent={messageId =>
								setMessages(prev =>
									prev.map(m =>
										m.id === messageId && m.reportIntent
											? {...m, reportIntent: {...m.reportIntent, status: 'cancelled'}}
											: m
									)
								)
							}
							onViewReportIntent={(payload, messageId, savedReportId) => {
								if (!canOpenReportBuilder()) return;
								// Once the intent has become a real report, always open THAT. The payload is a
								// snapshot of what the agent proposed and drifts the moment the report is edited,
								// so rebuilding a draft from it here would quietly discard the saved report.
								if (savedReportId) {
									history?.push(`/report-builder/${savedReportId}`);
									return;
								}
								// Never saved: the payload is the only thing that can open the builder at all. The
								// intent is ephemeral, so it rides along in location state rather than the URL (the
								// page redirects home if absent). The message id rides with it so the builder can
								// report the new report's id back to this card on its first save.
								history?.push('/report-builder', {
									initialConfig: payload as unknown as ReportIntentPayload,
									userId: viewerPersonId != null ? String(viewerPersonId) : undefined,
									taxonomy: reportTaxonomy,
									intentMessageId: messageId,
								});
							}}
							onPickReportCandidate={handlePickReportCandidate}
							reportCandidatesDisabled={isStreaming || isHitlPending}
							reportPickApplying={reportPick?.messageId === msg.id && reportPick.phase === 'applying'}
							reportPickFailed={reportPick?.messageId === msg.id && reportPick.phase === 'failed'}
							reportPickFailedMessage={
								reportPick?.messageId === msg.id && reportPick.phase === 'failed'
									? reportPick.failedMessage
									: undefined
							}
						/>
					)
				)}

				{introVariant === 'reportBuilder' && !reportBuilderIntroDismissed && (
					<ChatIntro
						bodyId="nova.report_builder_intro.body"
						suggestionIds={REPORT_BUILDER_SUGGESTION_IDS}
						onSuggestionClick={sendText}
					/>
				)}
				<div ref={messagesEndRef} />

				{showScrollBtn && (
					<ScrollToBottomBtn type="button" onClick={scrollToBottom}>
						↓
					</ScrollToBottomBtn>
				)}
			</MessagesArea>

			<DrawerFooter
				input={input}
				isStreaming={isStreaming}
				isHitlPending={isHitlPending}
				isSendBlocked={pickApplying}
				onInputChange={setInput}
				onSend={handleSend}
				onStop={abort}
				onKeyDown={handleKeyDown}
				inputRef={inputRef}
				onSavePrompt={handleSavePrompt}
			/>
		</PanelWrapper>
	);
};

export default ChatPanel;
